import DetailsCard from "@/components/DetailsCard";
import DetailsCardThreeColumnRow from "@/components/DetailsCardThreeColumnRow";
import EmptyState from "@/components/EmptyState";
import { MetricListItem, MetricListItemGrid } from "@/components/ListItem";
import type { MaterialNames } from "@/components/material_symbol_names";
import { formatDate } from "@/pages/_layouts/jobs/_utils";
import {
  CONSULTANT_SUMMARY_LABELS,
  formatSummaryHeadline,
  getCareerIntentSummaryCards,
  getCareerIntentSummaryRows,
  type CareerIntentSummaryCardId,
  type CareerIntentSummaryRow
} from "./careerIntentCatalog";
import { getCareerGoalRowIcon } from "./careerPlanHubSummaries";
import type { CareerPlanGoal } from "./careerPlanHistory";

type CareerGoalsDetailProps = {
  goal: CareerPlanGoal;
};

const CARD_EMPTY_STATES: Record<
  CareerIntentSummaryCardId,
  { iconName: MaterialNames; title: string; caption: string }
> = {
  careerRole: {
    iconName: "track_changes",
    title: "No Career Role Answers",
    caption: "This person has not saved areas to improve, target positions, or motivations."
  },
  location: {
    iconName: "location_on",
    title: "No Location Answers",
    caption: "This person has not saved states or facility types."
  },
  additionalDetails: {
    iconName: "sticky_note_2",
    title: "No Additional Details",
    caption: "This person has not saved retirement plans or additional context."
  }
};

function summaryHeadline(rows: CareerIntentSummaryRow[], label: string): string {
  const row = rows.find((item) => item.label === label);
  return (row ? formatSummaryHeadline(row.values) : "") || "--";
}

export function CareerGoalsDetail({ goal }: CareerGoalsDetailProps) {
  const rows = getCareerIntentSummaryRows(goal, true);
  const cards = getCareerIntentSummaryCards(rows);
  const archivedOn = goal.archivedAt ? formatDate(goal.archivedAt) : "";
  const updatedOn = formatDate(goal.updatedAt);

  return (
    <div className="flex min-w-0 w-full flex-col gap-6" data-testid={`career-goals-detail-${goal.id}`}>
      <MetricListItemGrid className="-mt-2" testId="career-goals-metrics">
        <MetricListItem
          iconName={getCareerGoalRowIcon(CONSULTANT_SUMMARY_LABELS.exploring)}
          overline="Exploring"
          headline={summaryHeadline(rows, CONSULTANT_SUMMARY_LABELS.exploring)}
        />
        <MetricListItem
          iconName={getCareerGoalRowIcon(CONSULTANT_SUMMARY_LABELS.timeframe)}
          overline="Timeframe"
          headline={summaryHeadline(rows, CONSULTANT_SUMMARY_LABELS.timeframe)}
        />
        <MetricListItem
          iconName={getCareerGoalRowIcon(CONSULTANT_SUMMARY_LABELS.clarity)}
          overline="Clarity"
          headline={summaryHeadline(rows, CONSULTANT_SUMMARY_LABELS.clarity)}
        />
        <MetricListItem
          iconName={archivedOn ? "archive" : getCareerGoalRowIcon("Updated")}
          overline={archivedOn ? "Archived" : "Updated"}
          headline={archivedOn || updatedOn || "--"}
        />
      </MetricListItemGrid>
      <DetailsCardThreeColumnRow>
        {cards.map((card) => {
          const empty = CARD_EMPTY_STATES[card.id];

          return (
            <DetailsCard
              key={card.id}
              heading={card.heading}
              className="!items-stretch"
              options={card.rows.map((row) => ({
                iconName: getCareerGoalRowIcon(row.label),
                overline: row.label,
                headline: formatSummaryHeadline(row.values),
                longContent: true
              }))}
            >
              {card.rows.length === 0 ? (
                <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
                  <EmptyState iconName={empty.iconName} title={empty.title} caption={empty.caption} />
                </div>
              ) : null}
            </DetailsCard>
          );
        })}
      </DetailsCardThreeColumnRow>
    </div>
  );
}
